import { useEffect, useRef, useState } from "react";
import { SvgIcon } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useStyles } from "./info-tooltip-style";
import { handleEscapeKey } from "utils/helpers";

interface InfoTooltipProps {
  tipText: string;
  ariaLabel?: string;
  id?: string;
}

export const InfoTooltip = ({ tipText, ariaLabel = "More information", id }: InfoTooltipProps) => {
  const classes = useStyles();
  const [visible, setVisible] = useState(false);
  const [dismissedByEsc, setDismissedByEsc] = useState(false);
  const tooltipId = `tooltip-${id ?? Math.random().toString(36).slice(2, 9)}`;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const show = () => {
    if (!dismissedByEsc) {
      setVisible(true);
    }
  };
  const hide = () => setVisible(false);
  const toggle = () => setVisible((v) => !v);

  useEffect(() => {
    const handleKeyDown = handleEscapeKey(
      () => {
        hide();
        setDismissedByEsc(true);
      },
      { focusElement: buttonRef.current },
    );

    if (visible) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible]);

  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      hide();
    }
  };

  const handleMouseLeave = () => {
    hide();
    setDismissedByEsc(false);
  };

  return (
    <span
      className={classes.wrapper}
      onMouseEnter={show}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
    >
      <button
        ref={buttonRef}
        type="button"
        className={classes.iconButton}
        aria-label={ariaLabel}
        aria-describedby={visible ? tooltipId : undefined}
        aria-expanded={visible}
        onClick={toggle}
        onFocus={show}
      >
        <SvgIcon
          component={InfoOutlined}
          className={classes.icon}
          aria-hidden="true"
          focusable="false"
        />
      </button>

      {visible && (
        <span id={tooltipId} role="tooltip" className={classes.tooltip}>
          {tipText}
        </span>
      )}
    </span>
  );
};
